import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useDisplayErrorNotification } from 'hooks/useDisplayErrorNotification';
import { NoDataFoundError } from '../APIRequestError';
import { handleSentryErrors } from '../HandleSentryErrors';
import { useCurrentUserStore } from 'store/UserStore';

interface queryParams {
  sites?: string[];
  startDate: string;
  endDate: string;
  userID?: string;
}

type DateCounts = {
  [date: string]: number;
};

export type OrganizationClaimsCounts = {
  [uuid: string]: DateCounts;
};

interface useCountOrganizationClaimsQueryParams {
  queryKey: any[];
  fetchFunction: (
    params: queryParams
  ) => Promise<OrganizationClaimsCounts | null>;
  functionName: string;
  enabled: boolean;
  params: queryParams;
}

/**
 * Custom hook for fetching count data for organization claims within a date range.
 * used for the organization claims chart.
 */

export const useCountOrganizationClaimsQuery = ({
  queryKey,
  fetchFunction,
  params,
  functionName,
  enabled,
}: useCountOrganizationClaimsQueryParams): UseQueryResult<
  OrganizationClaimsCounts,
  Error
> => {
  const { handleNoDataFound } = useDisplayErrorNotification();
  const user = useCurrentUserStore();
  return useQuery<OrganizationClaimsCounts, Error>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKey,
    queryFn: async () => {
      const data = await fetchFunction(params);
      if (!data) {
        const error = new NoDataFoundError(
          `No data found for ${functionName}`,
          functionName,
          params
        );
        handleSentryErrors({
          error,
          functionName,
          params,
          displayNotification: false,
          handleNoDataFound,
          user,
        });
      }
      return data;
    },
    meta: {
      functionName: functionName,
    },
    enabled: enabled,
  });
};
